/**
 * InspectionDialog - Preenchimento, revisão e histórico de uma vistoria
 *
 * Regras (validadas também no banco):
 * - Pendente/Em andamento: executor preenche os itens (cada gravação = nova revisão).
 * - Aguardando revisão: respostas bloqueadas; revisor aprova/reprova item a item.
 * - Correção solicitada: somente itens reprovados ficam editáveis.
 * - Aprovada/Reprovada/Cancelada: somente leitura (reabertura com motivo).
 *
 * @version 1.28.0
 * RELEASE: 2026-10-05 v1.28.0 - Linha "Cliente" na lateral (snapshot da vistoria), clicável com
 *          ícone de olho para abrir a ficha quando o usuário pode ver Clientes.
 * RELEASE: 2026-10-03 v1.27.0 - Painel de aceites carregado em qualquer status (inclusive
 *          Aprovada/Reprovada/Cancelada) em modo somente leitura, para auditoria.
 * RELEASE: 2026-10-02 v1.26.0 - Lateral "Informações da vistoria" passa a exibir o
 *          Tipo do checklist do modelo usado (novo campo devolvido por
 *          get_checklist_instance v1.3.0), com fallback "—" quando o modelo não
 *          tem tipo vinculado.
 * RELEASE: 2026-09-28 v1.25.1 - Responsável interno passa a exibir nome e e-mail
 *          vigentes do perfil, evitando metadados antigos da autenticação.
 * RELEASE: 2026-09-28 v1.25.0 - Permite editar tipo e responsável, revogando o link externo anterior.
 * RELEASE: 2026-09-28 v1.24.0 - Foto modelo e seletor compartilhado de evidência nos itens visuais.
 * RELEASE: 2026-09-27 v1.23.1 - Restaura o botão textual de atualização de
 *          versão e move o anexo truncado para a linha de metadados, após revisões.
 * RELEASE: 2026-09-27 v1.23.0 - Remove as abas e adota o leiaute do modal de
 *          Proposta: itens sempre visíveis à esquerda, informações e histórico
 *          em lateral fixa; itens visuais passam ao máximo de três por linha.
 * RELEASE: 2026-10-10 v1.28.0 - Modelo sem revisão: libera aceites before_approve/before_complete
 *          em andamento e o botão vira "Concluir vistoria" (paridade com submit_checklist_instance).
 * RELEASE: 2026-09-27 v1.22.0 - PDF completo ganha preview interno protegido,
 *          mantendo o download separado no modal da vistoria.
 * RELEASE: 2026-09-27 v1.21.0 - Botão "Atualizar versão" no cabeçalho: visível
 *          quando existe versão publicada mais nova do modelo e a vistoria não
 *          está em status final. Confirmação explica o impacto (itens mantidos,
 *          removidos viram histórico, novos a preencher, aceites podem precisar
 *          ser refeitos). Servidor: upgrade_checklist_instance_version v1.0.0.
 * RELEASE: 2026-09-27 v1.20.0 - Faixa do link externo movida para logo abaixo do cabeçalho.
 * RELEASE: 2026-09-27 v1.19.0 - Botão "Coletar aceite" fica desabilitado enquanto
 *          houver itens pendentes (obrigatórios sem resposta, reprovados sem refazer
 *          ou aguardando análise), com tooltip explicando o que falta. Servidor
 *          revalida em confirm_checklist_acceptance v1.1.0.
 * RELEASE: 2026-09-27 v1.18.0 - Itens de arquivo/foto salvam automaticamente ao
 *          selecionar o arquivo (mesmo fluxo da captura de foto): upload + gravação
 *          da revisão disparam no onChange, sem botão "Salvar" nem estado
 *          "aguardando salvar". O ícone de salvar permanece apenas nos itens
 *          não-arquivo (texto, número, data, seleções).
 * RELEASE: 2026-09-27 v1.17.4 - Botão "Salvar item" substituído por ícone único de
 *          salvar no cabeçalho, liberando espaço para o tipo do item subir e ficar
 *          na mesma linha do status.
 * RELEASE: 2026-09-27 v1.17.3 - Item compactado: metadados (estado, tipo, localização,
 *          revisões e data) em uma única linha e zona de upload em linha única (h-10);
 *          com arquivo já escolhido, a caixa grande de upload é substituída pela lista
 *          compacta + atalho discreto "Trocar/Adicionar arquivo".
 * RELEASE: 2026-09-27 v1.17.2 - Item reprovado: a orientação "Refazer" vira um ícone
 *          "?" com tooltip junto ao status (deixa de ocupar um bloco de texto) e os
 *          arquivos pendentes ficam em linha única compacta (nome + tamanho + remover),
 *          sem repetir "aguardando salvar" já indicado na miniatura.
 * RELEASE: 2026-09-27 v1.17.1 - Conteúdo do item em largura total: motivo da reprovação,
 *          orientação de correção, botões e campos de preenchimento deixam de ficar
 *          confinados à coluna direita e passam a usar o espaço abaixo da miniatura,
 *          no mesmo padrão já aplicado à advertência de fraude e aos anexos.
 * RELEASE: 2026-09-27 v1.17.0 - Leiaute do item otimizado: o ícone de localização
 *          acompanha o tipo do item na linha de status (deixa de ocupar uma linha
 *          própria) e a advertência de fraude com o nome dos anexos ocupam a largura
 *          total abaixo da miniatura, aproveitando o espaço antes vazio à esquerda.
 * RELEASE: 2026-09-27 v1.15.0 - Barra do comprovante de aceite (situação e download).
 * RELEASE: 2026-09-27 v1.14.0 - Painel de aceites exigidos (Core de Aceites): coleta por etapa e
 *          bloqueio de Enviar/Concluir revisão enquanto houver aceite obrigatório pendente.
 * RELEASE: 2026-09-26 v1.13.1 - Itens de documento sem anexo deixam de exibir
 *          miniatura vazia duplicada; seletor ocupa a largura útil e mantém uma
 *          composição compacta, proporcional aos demais campos da vistoria.
 * RELEASE: 2026-09-26 v1.13.0 - Metadados do item separados em status/tipo e
 *          revisão/data salva; matriz adaptativa usa até quatro colunas para
 *          evidências e preserva mais largura para campos textuais e seleções.
 * RELEASE: 2026-09-26 v1.12.0 - Linha do tempo ganha visual premium (badges
 *          coloridos com ícone, conector vertical e agrupamento por dia, mesmo
 *          padrão da timeline de propostas); cabeçalho exibe "Criada em" e,
 *          quando concluída, "Concluída em"; nos itens em revisão os botões
 *          Aprovar/Reprovar ficam lado a lado e o motivo vem após o Reprovar.
 * RELEASE: 2026-09-26 v1.11.0 - Documentos abrem no preview interno e o
 *          seletor nativo foi substituído por uma área compacta de upload.
 * RELEASE: 2026-09-26 v1.10.0 - Placa abre o modal do veículo com a mesma
 *          interação do Kanban, condicionada à permissão vehicles_list.
 * RELEASE: 2026-09-26 v1.9.1 - Ações de revisão passam a refletir a decisão
 *          vigente: item aprovado exibe apenas reprovação; item reprovado,
 *          apenas aprovação; item sem decisão mantém as duas alternativas.
 * RELEASE: 2026-09-26 v1.9.0 - Filtros rápidos por status (pílulas com contador); botões
 *          Enviar/Concluir revisão só habilitam sem pendências obrigatórias (servidor revalida);
 *          "N revisões" abre a linha do tempo de revisões do item.
 * RELEASE: 2026-09-26 v1.8.0 - Placa Mercosul movida para componente compartilhado
 *          com o card do Kanban, sem alterar o leiaute do modal.
 * RELEASE: 2026-09-26 v1.7.0 - Alertas estruturados só aparecem quando todas as
 *          evidências ainda pertencem às revisões vigentes; selo Aprovado fica mais
 *          compacto e o envio para revisão usa o tom semântico de atenção.
 * RELEASE: 2026-09-26 v1.6.0 - Itens de foto por câmera salvam automaticamente
 *          após a confirmação; captura fica bloqueada até iniciar a vistoria e
 *          nova foto substitui a vigente quando o limite é um, preservando revisões.
 * RELEASE: 2026-09-26 v1.4.0 - Modal desktop ampliado para 95vw x 97vh e grade
 *          responsiva de até três colunas; pins abrem a coordenada no Google Maps;
 *          alertas antifraude identificam, navegam e destacam os itens/anexos afetados.
 * RELEASE: 2026-09-26 v1.3.0 - Ajustes visuais aprovados: status da vistoria passa a
 *          usar bolinha colorida + texto (StatusDotLabel) no lugar do Badge balão;
 *          botões "Aprovar" (item) e "Concluir revisão" (rodapé) em tom de sucesso
 *          (esmeralda); barra de progresso esmeralda ao concluir 100%; cabeçalho
 *          mobile reorganizado em 3 linhas (título+status / placa+veículo /
 *          versão+responsável+prazo+progresso) eliminando o espaço vazio à direita.
 * RELEASE: 2026-09-26 v1.2.0 - Redesign premium "Premium graphite amber" (direção escolhida
 *          pelo usuário): cabeçalho compacto em faixa única com placa estilo Mercosul,
 *          prazo e progresso alinhados; abas com sublinhado; seções com contador em pill;
 *          cards de item com miniatura grande à esquerda e botões menores com hierarquia
 *          clara (principal sólido, secundário outline, destrutivo discreto); rodapé fixo
 *          enxuto com ações lado a lado no desktop. Lógica e chamadas RPC inalteradas.
 * RELEASE: 2026-09-26 v1.1.0 - Layout premium "lista com miniaturas grandes": cabeçalho com
 *          placa/prazo/progresso, seções com barra de progresso, cards com miniatura (foto
 *          assinada/lightbox), selos de estado e rodapé fixo. Lógica inalterada.
 * RELEASE: 2026-09-27 v1.22.0 - Responsável do tipo Proprietário vira atalho que abre a ficha
 *          do cliente/contato (CustomerDetailsModalPremium), usando customer_id/contact_id
 *          congelados no snapshot da vistoria.
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Check, X, AlertTriangle, Paperclip, Loader2, Camera, Clock, ImageOff, FileText, ExternalLink, PlayCircle, Upload, HelpCircle, Save, RefreshCw, Pencil, Eye } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { CameraCaptureDialog } from './CameraCaptureDialog';
import { ItemRevisionsTimelineDialog } from './ItemRevisionsTimelineDialog';
import { InspectionTimeline } from './InspectionTimeline';
import { MapLinkButton } from './MapLinkButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { StatusDotLabel } from '@/components/ui/status-dot-label';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { VehiclePlateChip } from '@/components/vehicles/VehiclePlateChip';
import { VehicleDetailsModalPremium } from '@/components/vehicles/modal/VehicleDetailsModalPremium';
// v1.22.0: ficha do cliente/contato aberta a partir do responsável (modo Proprietário)
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getFileColor, getFileIcon, getMimeMediaType } from '@/lib/file-utils';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { fieldTypeLabel } from '@/hooks/useChecklistTemplates';
import {
  INSPECTION_STATUS, FRAUD_LABEL, DEADLINE_LABEL, captureGeo, uploadInspectionFile, signedInspectionUrl,
  useInspectionActions, useInspectionDetail, useInspectionTimeline, useInspectionRealtime, type InspectionDetail, type InspectionItem,
  type InspectionFraudFlag,
} from '@/hooks/useVehicleInspections';
import { useChecklistAcceptance, type AcceptanceStage } from '@/hooks/useChecklistAcceptance';
import { AcceptanceParticipantsPanel, AcceptanceReceiptBar, pendingAcceptances } from '@/components/acceptance/AcceptanceParticipantsPanel';
import { InspectionReportBar } from './InspectionReportBar';
import { InspectionExternalLinkBar } from './InspectionExternalLinkBar';
import { InspectionEvidenceDrawer } from './InspectionEvidenceDrawer';
import { InspectionAssigneeDialog, type InspectionAssigneeMode } from './InspectionAssigneeDialog';

interface Props {
  instanceId: string | null;
  onOpenChange: (open: boolean) => void;
}

// v1.12.0: rótulos de eventos da linha do tempo migraram para ./InspectionTimeline

const fmt = (d?: string | null) => (d ? format(new Date(d), "dd/MM/yyyy HH:mm", { locale: ptBR }) : '—');

/** Linha compacta de informação usada na lateral da vistoria. */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-xs font-semibold text-foreground text-right break-words">{value}</span>
    </div>
  );
}

/** Traduz o modo congelado de atribuição para uma descrição operacional. */
function responsibleTypeLabel(mode?: string | null): string {
  if (mode === 'provider') return 'Prestador';
  if (mode === 'owner') return 'Proprietário';
  if (mode === 'queue') return 'Fila global';
  return 'Usuário interno';
}

/** Resolve o nome exibido sem trocar o vínculo histórico da vistoria. */
function responsibleName(data: InspectionDetail): string {
  if (data.instance.assignee_mode === 'provider') return data.instance.provider_name ?? 'Prestador da O.S';
  if (data.instance.assignee_mode === 'owner') return data.vehicle.owner_name ?? 'Proprietário do veículo';
  return data.instance.executor_name ?? '—';
}

/** Retorna o e-mail do executor apenas nos modos vinculados a usuário. */
function responsibleEmail(data: InspectionDetail): string | null {
  if (data.instance.assignee_mode === 'owner' || data.instance.assignee_mode === 'provider') return null;
  return data.instance.executor_email ?? null;
}

/** Exibe o valor de uma resposta em modo leitura */
function displayValue(v: any, type: string) {
  if (v === null || v === undefined || v === '') return '—';
  if (type === 'yes_no') return v ? 'Sim' : 'Não';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

/** Item conta como "completo" quando já possui resposta salva */
const isDone = (it: InspectionItem) => !!it.current;

/** Resposta válida para itens obrigatórios (mesma regra do servidor: valor ou anexo). */
const hasValidAnswer = (it: InspectionItem) => {
  if (!it.current) return false;
  if (it.attachments.length > 0) return true;
  const v = it.current.value;
  return !(v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0));
};

/** Filtros rápidos por status dentro do modal. */
type ItemFilter = 'all' | 'pending' | 'answered' | 'awaiting' | 'approved' | 'rejected' | 'fraud';
const ITEM_FILTERS: { key: ItemFilter; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'pending', label: 'Pendentes' },
  { key: 'answered', label: 'Respondidos' },
  { key: 'awaiting', label: 'Aguardando análise' },
  { key: 'approved', label: 'Aprovados' },
  { key: 'rejected', label: 'Reprovados' },
  { key: 'fraud', label: 'Com alerta' },
];

/**
 * Define a largura do card pela densidade de interação do tipo de item.
 * Evidências visuais cabem em três colunas; campos textuais preservam espaço.
 */
function itemGridSpan(fieldType: string) {
  if (['photo', 'file', 'photo_or_file'].includes(fieldType)) {
    return 'col-span-12 sm:col-span-6 xl:col-span-4';
  }
  if (['number', 'yes_no', 'date', 'time', 'datetime'].includes(fieldType)) {
    return 'col-span-12 md:col-span-6 lg:col-span-4';
  }
  return 'col-span-12 md:col-span-6';
}

// v1.12.0: validação de coordenadas e botão de mapa extraídos para ./MapLinkButton

/** Texto objetivo para o revisor entender o motivo sem abrir detalhes técnicos. */
function fraudDetail(flag: InspectionFraudFlag) {
  if (flag.type === 'distance' && flag.max_m) return `${flag.max_m} m entre registros; limite ${flag.limit_m ?? '—'} m`;
  if (flag.type === 'low_accuracy' && flag.accuracy_m) return `Precisão aproximada de ${flag.accuracy_m} m`;
  if (flag.type === 'clock_skew' && flag.skew_seconds) return `Relógio divergente em ${Math.round(flag.skew_seconds / 60)} min`;
  return null;
}

/** Barra de progresso simples (primária; esmeralda ao concluir 100%) */
function ProgressBar({ value, className }: { value: number; className?: string }) {
  return (
    <div className={cn('h-1.5 bg-muted rounded-full overflow-hidden', className)}>
      <div className={cn('h-full transition-all', value >= 1 ? 'bg-emerald-500' : 'bg-primary')} style={{ width: `${Math.round(value * 100)}%` }} />
    </div>
  );
}

/** Gera URL assinada (com cache simples em memória) para miniaturas do bucket privado */
const urlCache = new Map<string, string>();
function useSignedUrl(path?: string | null) {
  const [url, setUrl] = useState<string | null>(path ? urlCache.get(path) ?? null : null);
  useEffect(() => {
    if (!path || urlCache.has(path)) return;
    let alive = true;
    signedInspectionUrl(path).then((u) => { if (u) { urlCache.set(path, u); if (alive) setUrl(u); } });
    return () => { alive = false; };
  }, [path]);
  return path ? url ?? urlCache.get(path) ?? null : null;
}

export function InspectionDialog({ instanceId, onOpenChange }: Props) {
  const { currentTenant, user } = useAuth();
  const { canView, canEdit } = usePermissions();
  const { data, isLoading } = useInspectionDetail(instanceId);
  // O histórico permanece visível na lateral durante toda a consulta da vistoria.
  const { data: timeline } = useInspectionTimeline(instanceId, !!instanceId);
  const actions = useInspectionActions();
  const [reason, setReason] = useState('');
  // Em status finais o campo de motivo só aparece após clicar em "Reabrir"
  const [reopenMode, setReopenMode] = useState(false);
  // v1.21.0: confirmação da troca de versão da vistoria
  const [confirmUpgrade, setConfirmUpgrade] = useState(false);
  const [highlightedAnswerId, setHighlightedAnswerId] = useState<string | null>(null);
  // A ficha do veículo só abre para perfis com acesso à listagem de veículos.
  const [vehicleModalId, setVehicleModalId] = useState<string | null>(null);
  const canOpenVehicle = canView('vehicles_list');
  // v1.22.0: atalho para a ficha do cliente/contato quando o responsável é o Proprietário.
  // Usa o customer_id/contact_id congelados no snapshot da vistoria (contexto histórico).
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [assigneeDialogOpen, setAssigneeDialogOpen] = useState(false);
  const ownerCustomerId = (data?.vehicle?.customer_id as string | undefined) ?? null;
  const ownerContactId = (data?.vehicle?.contact_id as string | undefined) ?? null;
  const canOpenOwner = data?.instance.assignee_mode === 'owner' && !!(ownerCustomerId || ownerContactId);
  // v1.28.0: linha "Cliente" — link somente com permissão de Clientes e vínculo no snapshot.
  const ownerName = (data?.vehicle?.owner_name as string | undefined) || '—';
  const canOpenCustomer = canView('crm_customers') && !!(ownerCustomerId || ownerContactId);
  const canEditAssignee = canEdit('checklist_inspections')
    && data?.instance.assignee_mode !== 'provider'
    && ['pending', 'in_progress', 'correction_requested'].includes(data?.instance.status ?? '');

  const status = data?.instance.status;
  const settings = data?.version.settings ?? {};
  const geoPerItem = ['item', 'item_and_submit'].includes(settings.geo_mode);
  const geoOnSubmit = ['submit', 'item_and_submit'].includes(settings.geo_mode);

  // Agrupa itens por seção congelada
  const groups = useMemo(() => {
    if (!data) return [];
    const bySection = new Map<string | null, InspectionItem[]>();
    data.items.forEach((it) => {
      const k = it.item.version_section_id;
      bySection.set(k, [...(bySection.get(k) ?? []), it]);
    });
    const out: { name: string; items: InspectionItem[] }[] = [];
    if (bySection.get(null)?.length) out.push({ name: 'Geral', items: bySection.get(null)! });
    data.sections.forEach((s) => bySection.get(s.id)?.length && out.push({ name: s.name, items: bySection.get(s.id)! }));
    return out;
  }, [data]);

  // Itens reprovados e filtro rápido "Somente reprovados"
  const rejectedCount = data?.items.filter((i) => i.review_status === 'rejected').length ?? 0;
  const [itemFilter, setItemFilter] = useState<ItemFilter>('all');
  const [revisionsFor, setRevisionsFor] = useState<{ id: string; name: string } | null>(null);

  // Tempo real: atualiza o modal quando outro usuário altera a vistoria
  useInspectionRealtime(instanceId);

  const total = data?.items.length ?? 0;
  const done = data?.items.filter(isDone).length ?? 0;
  // Alertas estruturados deixam de ser correntes quando qualquer evidência foi substituída.
  // Alertas legados sem revisão individual continuam visíveis como resumo histórico.
  const currentFraudFlags = useMemo(() => {
    if (!data) return [];
    const currentRevisionByAnswer = new Map(data.items.map((item) => [item.answer_id, item.current?.id]));
    return (data.instance.fraud_flags ?? []).filter((flag) => {
      if (!flag.evidences?.length) return true;
      return flag.evidences.every((evidence) =>
        evidence.kind !== 'item'
        || !evidence.revision_id
        || (!!evidence.answer_id && currentRevisionByAnswer.get(evidence.answer_id) === evidence.revision_id));
    });
  }, [data]);
  // Itens com evidência suspeita (alertas vigentes)
  const fraudAnswerIds = useMemo(() => new Set(currentFraudFlags.flatMap((f) =>
    (f.evidences ?? []).map((e) => e.answer_id).filter(Boolean) as string[])), [currentFraudFlags]);

  /** Aplica um filtro rápido a um item. */
  const matchFilter = (it: InspectionItem, f: ItemFilter) => {
    switch (f) {
      case 'pending': return !isDone(it) || (status === 'correction_requested' && it.review_status === 'rejected');
      case 'answered': return isDone(it);
      case 'awaiting': return isDone(it) && it.item.requires_approval && it.review_status === 'pending';
      case 'approved': return it.review_status === 'approved';
      case 'rejected': return it.review_status === 'rejected';
      case 'fraud': return fraudAnswerIds.has(it.answer_id);
      default: return true;
    }
  };
  const filterCounts = useMemo(() => Object.fromEntries(ITEM_FILTERS.map(({ key }) =>
    [key, (data?.items ?? []).filter((it) => matchFilter(it, key)).length])) as Record<ItemFilter, number>,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [data, status, fraudAnswerIds]);
  const visibleGroups = useMemo(() => itemFilter === 'all' ? groups
    : groups.map((g) => ({ ...g, items: g.items.filter((i) => matchFilter(i, itemFilter)) })).filter((g) => g.items.length),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [groups, itemFilter, status, fraudAnswerIds]);

  // Regras de liberação dos botões (servidor revalida em submit/finalize)
  const missingRequired = data?.items.filter((it) => it.item.is_required && !hasValidAnswer(it)).length ?? 0;
  const rejectedPendingFix = status === 'correction_requested' ? rejectedCount : 0;
  const reviewPending = data?.items.filter((it) => it.item.requires_approval && it.review_status === 'pending').length ?? 0;
  // v1.14.0: aceites exigidos pela política do modelo (servidor revalida em submit/finalize)
  const submitStage = status === 'in_progress' || status === 'correction_requested';
  // v1.28.0: modelo sem revisão ("Exige revisão antes de aprovar" desmarcado) conclui direto no envio;
  // libera também os aceites de aprovação/conclusão, igual a submit_checklist_instance v1.3.0
  const requiresReview = settings.requires_review !== false;
  const acceptanceStages: AcceptanceStage[] = submitStage
    ? (requiresReview ? ['before_submit'] : ['before_submit', 'before_approve', 'before_complete'])
    : status === 'awaiting_review' ? ['before_approve', 'before_complete'] : [];
  // isError: falha ao carregar aceites passa a ser avisada (antes o painel apenas sumia)
  // v1.27.0: carrega em qualquer status para auditoria; em status finais acceptanceStages
  // fica vazio e o painel vira somente leitura (sem "Coletar aceite")
  const { data: acceptance, isError: acceptanceLoadError } = useChecklistAcceptance(instanceId, !!status);
  const acceptancePending = pendingAcceptances(acceptance, acceptanceStages);
  // v1.19.0: aceite só pode ser coletado depois que os itens estiverem resolvidos
  // (servidor revalida em confirm_checklist_acceptance v1.1.0)
  const acceptanceBlockReason = submitStage
    ? (missingRequired > 0 ? `Preencha os itens obrigatórios antes de coletar o aceite (faltam ${missingRequired})`
      : rejectedPendingFix > 0 ? `Refaça os itens reprovados antes de coletar o aceite (restam ${rejectedPendingFix})` : '')
    // v1.28.1: sem revisão não há quem analise os itens; servidor (confirm_checklist_acceptance v1.6.0) também não exige
    : requiresReview && reviewPending > 0 ? `Aprove ou reprove todos os itens antes de coletar o aceite (restam ${reviewPending})` : '';
  const reviewBlockMsg = reviewPending > 0 ? `Faltam ${reviewPending} item(ns) para aprovar ou reprovar`
    : acceptancePending > 0 ? `Falta(m) ${acceptancePending} aceite(s)` : '';
  const submitBlockers = missingRequired + rejectedPendingFix + (submitStage ? acceptancePending : 0);
  const submitBlockMsg = missingRequired > 0
    ? `Faltam ${missingRequired} item(ns) obrigatório(s)`
    : rejectedPendingFix > 0 ? `Faltam ${rejectedPendingFix} item(ns) reprovado(s) para refazer`
    : acceptancePending > 0 ? `Falta(m) ${acceptancePending} aceite(s)` : '';

  const deadlineStatus: string | undefined = data?.instance.deadline_status;
  const dueAt: string | null = data?.instance.finish_due_at ?? data?.instance.due_at ?? data?.instance.start_due_at ?? null;
  const overdue = deadlineStatus === 'start_overdue' || deadlineStatus === 'finish_overdue';

  // A vistoria precisa ser iniciada explicitamente antes de qualquer preenchimento.
  const canEditItem = (it: InspectionItem) =>
    status === 'in_progress' || (status === 'correction_requested' && it.review_status === 'rejected');

  const handleSubmit = async () => {
    const geo = geoOnSubmit ? await captureGeo() : null;
    if (geoOnSubmit && settings.geo_required && !geo) return toast.error('Permita o acesso à localização para enviar');
    actions.submit.mutate({ p_instance_id: instanceId, p_geo: geo });
  };

  // v1.5.0: o motivo só é limpo após sucesso da ação (clear passado ao callback)
  const askReason = (label: string, fn: (r: string, clear: () => void) => void) => {
    if (reason.trim().length < 3) return toast.error(`Informe o motivo para ${label}`);
    fn(reason.trim(), () => setReason(''));
  };

  /** Leva o revisor diretamente ao cartão que originou o alerta. */
  const focusEvidence = (answerId?: string) => {
    if (!answerId) return;
    setHighlightedAnswerId(answerId);
    window.setTimeout(() => document.getElementById(`inspection-item-${answerId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
    window.setTimeout(() => setHighlightedAnswerId((current) => current === answerId ? null : current), 2400);
  };

  return (
    <TooltipProvider>
    <Dialog open={!!instanceId} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen max-w-none h-[100dvh] rounded-none border-0 md:max-w-[95vw] md:w-[95vw] md:h-[97vh] md:rounded-lg md:border flex flex-col p-0 gap-0 overflow-hidden">
        {/* Cabeçalho compacto em faixa única: título+status, placa Mercosul, dados do veículo, prazo e progresso */}
        <DialogHeader className="px-4 md:px-6 py-3 md:py-4 bg-muted/40 border-b text-left space-y-2">
          {/* Linha 1: título + status (bolinha colorida + texto) */}
          <DialogTitle className="flex flex-wrap items-center gap-x-2 gap-y-1 text-base md:text-lg font-bold leading-tight pr-6">
            {/* v1.12.1: status como primeiro elemento da linha, antes do título; v1.12.2: separadores "|" entre as informações */}
            {status && <StatusDotLabel tone={INSPECTION_STATUS[status].tone} label={INSPECTION_STATUS[status].label} className="font-normal" />}
            <span className="text-xs font-normal text-muted-foreground select-none" aria-hidden="true">|</span>
            <span className="min-w-0">Vistoria #{data?.instance.code ?? '...'} — {data?.instance.template_name}</span>
          </DialogTitle>
          {data && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-3">
               {/* Linha 2 (mobile) / coluna esquerda (desktop): placa + veículo */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground min-w-0">
                 <VehiclePlateChip
                   plate={data.vehicle.plate}
                   onOpenVehicle={canOpenVehicle && data.vehicle.vehicle_id
                     ? () => setVehicleModalId(String(data.vehicle.vehicle_id))
                     : undefined}
                 />
                <span className="font-medium text-foreground">
                  {[data.vehicle.brand, data.vehicle.model, data.vehicle.year].filter(Boolean).join(' ')}
                </span>
               </div>
              {/* Linha 3 (mobile) / coluna direita (desktop): prazo + progresso */}
              <div className="flex items-center gap-4 md:gap-5 shrink-0 md:text-right">
                <div className="md:text-right">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Prazo</p>
                  <p className={cn('text-xs font-semibold', overdue ? 'text-destructive' : 'text-foreground')}>
                    {dueAt ? fmt(dueAt) : 'Livre'}{overdue && deadlineStatus ? ` • ${DEADLINE_LABEL[deadlineStatus]}` : ''}
                  </p>
                </div>
                <div className="flex-1 md:flex-none md:text-right">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Progresso</p>
                  <div className="flex items-center gap-2">
                    <ProgressBar value={total ? done / total : 0} className="flex-1 md:flex-none md:w-20" />
                    <span className="text-xs font-bold">{done}/{total}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogHeader>

        {/* v1.21.0: faixa do link externo visível em todos os status; ações variam pelo status */}
        {data && (
          <InspectionExternalLinkBar instanceId={data.instance.id} code={data.instance.code}
            status={data.instance.status} canManage={canEdit('checklist_inspections')} />
        )}

        {isLoading || !data ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Corpo no padrão da Proposta: itens à esquerda e informações/histórico à direita. */}
            <div className="flex-1 min-h-0 flex flex-col xl:flex-row overflow-y-auto xl:overflow-hidden">
              <main className="flex-1 min-w-0 xl:min-h-0 xl:overflow-y-auto scrollbar-thin px-4 md:px-6 py-5 space-y-6 bg-muted/30">
              {currentFraudFlags.length > 0 && (
                <div className="border-l-2 border-destructive pl-3 py-1 text-sm">
                  <div className="flex items-center gap-1 font-medium text-destructive"><AlertTriangle className="h-4 w-4" />Alertas de fraude</div>
                  <div className="mt-1.5 space-y-1.5">
                    {currentFraudFlags.map((f, i) => {
                      const evidences = f.evidences ?? [];
                      const names = [...new Set(evidences.map((e) => e.item_name).filter(Boolean))];
                      return (
                        <div key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                          <span>
                            {FRAUD_LABEL[f.type] ?? f.type}
                            {fraudDetail(f) ? ` — ${fraudDetail(f)}` : ''}
                            {names.length ? ` — ${names.join(' × ')}` : f.item ? ` — ${f.item}` : ''}
                          </span>
                          {evidences.map((e, evidenceIndex) => (
                            <div key={`${e.answer_id ?? e.kind}-${evidenceIndex}`} className="inline-flex items-center gap-1">
                              {e.answer_id && (
                                <Button type="button" size="sm" variant="link" className="h-5 px-0 text-[11px]"
                                  onClick={() => focusEvidence(e.answer_id)}>
                                  Ver evidência <ExternalLink className="ml-1 h-3 w-3" />
                                </Button>
                              )}
                              <MapLinkButton lat={e.lat} lng={e.lng} />
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Aviso ao revisor: itens reprovados só voltam ao vistoriador após concluir a revisão */}
              {status === 'awaiting_review' && rejectedCount > 0 && (
                <div className="border-l-2 border-destructive pl-3 py-1 text-xs flex flex-wrap items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                  <span><b>{rejectedCount}</b> item(ns) reprovado(s). Clique em <b>Concluir revisão</b> para devolver ao vistoriador tirar novas fotos.</span>
                </div>
              )}
              {status === 'correction_requested' && rejectedCount > 0 && (
                <div className="border-l-2 border-destructive pl-3 py-1 text-xs flex flex-wrap items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                  <span><b>{rejectedCount}</b> item(ns) para refazer. Registre novas evidências e depois clique em <b>{requiresReview ? 'Enviar para revisão' : 'Concluir vistoria'}</b>.</span>
                  <Button size="sm" variant="outline" className="h-6 text-[11px] ml-auto" onClick={() => setItemFilter((v) => v === 'rejected' ? 'all' : 'rejected')}>
                    {itemFilter === 'rejected' ? 'Mostrar todos' : 'Somente reprovados'}
                  </Button>
                </div>
              )}

              {/* v1.15.0: comprovante do aceite concluído */}
              {status && status !== 'pending' && <AcceptanceReceiptBar instanceId={data.instance.id} />}
              {/* v1.16.0: PDF completo da vistoria (itens, fotos, anexos e aceites) */}
              {(status === 'approved' || status === 'rejected') && (
                <InspectionReportBar
                  instanceId={data.instance.id}
                  title={`Vistoria #${data.instance.code} — ${data.instance.template_name}`}
                />
              )}
              {/* v1.14.0: aceites exigidos pela política do modelo */}
              {acceptanceLoadError && (
                <p className="rounded-md border-l-2 border-destructive pl-3 text-xs text-destructive">
                  Não foi possível carregar os aceites desta vistoria. Feche e abra novamente.
                </p>
              )}
              {acceptance?.required && (
                <AcceptanceParticipantsPanel data={acceptance} openStages={acceptanceStages} instanceId={instanceId ?? undefined}
                  ownerName={data?.vehicle?.owner_name ?? undefined}
                  blockedReason={acceptanceBlockReason || undefined}
                  defaultName={(user?.user_metadata?.full_name as string | undefined) ?? ''} />
              )}

              {/* Filtros rápidos por status (pílulas com contador, padrão da aba Financeiro) */}
              <div className="flex flex-wrap items-center gap-1.5">
                {ITEM_FILTERS.map(({ key, label }) => (
                  <button key={key} type="button" onClick={() => setItemFilter(key)}
                    className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 h-7 text-xs font-medium transition-colors',
                      itemFilter === key ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground hover:bg-muted')}>
                    {label}
                    <span className={cn('rounded-full px-1.5 text-[10px] font-bold', itemFilter === key ? 'bg-primary-foreground/20' : 'bg-muted')}>{filterCounts[key]}</span>
                  </button>
                ))}
              </div>
              {visibleGroups.length === 0 && <p className="text-sm text-muted-foreground">Nenhum item neste filtro.</p>}

              {visibleGroups.map((g) => (
                <section key={g.name} className="space-y-3">
                  {/* Cabeçalho da seção: título discreto + contador em pill */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">{g.name}</h3>
                    <span className="text-[10px] font-semibold text-muted-foreground bg-background border rounded-full px-2 py-0.5">
                      {g.items.filter(isDone).length} / {g.items.length} completos
                    </span>
                  </div>
                  {/* Matriz de 12 colunas: a largura varia conforme a complexidade do campo. */}
                  <div className="grid grid-cols-12 items-start gap-3">
                    {g.items.map((it) => (
                      <div key={it.answer_id} className={cn('min-w-0', itemGridSpan(it.item.field_type))}>
                        <ItemRow
                          it={it}
                          editable={canEditItem(it)}
                          reviewing={status === 'awaiting_review'}
                          pendingStart={status === 'pending'}
                          geoPerItem={geoPerItem}
                          geoRequired={!!settings.geo_required}
                          tenantId={currentTenant?.id ?? ''}
                          instanceId={data.instance.id}
                          actions={actions}
                          fraudFlags={currentFraudFlags.filter((flag) =>
                            flag.evidences?.some((evidence) => evidence.answer_id === it.answer_id))}
                          highlighted={highlightedAnswerId === it.answer_id}
                          onOpenRevisions={() => setRevisionsFor({ id: it.answer_id, name: it.item.name })}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
              </main>

              {/* Lateral permanente: dados operacionais no topo e histórico com rolagem própria. */}
              <aside className="xl:w-[360px] shrink-0 xl:min-h-0 flex flex-col xl:overflow-hidden border-t xl:border-t-0 xl:border-l border-border bg-muted/20">
                <div className="shrink-0 px-5 pt-5 pb-4 space-y-3">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Informações da vistoria</h3>
                  <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
                    <InfoRow label="Criada em" value={fmt(data.instance.created_at)} />
                    <InfoRow label="Concluída em" value={fmt(data.instance.finished_at)} />
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-muted-foreground">Versão</span>
                      <div className="flex items-center justify-end gap-1.5 min-w-0">
                        <span className="text-xs font-semibold text-foreground">v{data.version.version_number}</span>
                        {data.version.has_newer_version && !['approved', 'rejected', 'cancelled'].includes(status ?? '') && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button type="button" size="sm" variant="outline" className="h-7 shrink-0 gap-1 px-2 text-[11px]"
                                disabled={actions.upgrade.isPending} onClick={() => setConfirmUpgrade(true)}>
                                {actions.upgrade.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                Atualizar para v{data.version.latest_version_number}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Aplica as correções mais recentes do modelo nesta vistoria</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    {/* v1.26.0: Tipo do checklist do modelo usado na vistoria */}
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-xs text-muted-foreground shrink-0">Tipo Checklist</span>
                      <span className="text-xs font-semibold text-foreground text-right break-words">
                        {data.instance.checklist_type_name || '—'}
                      </span>
                    </div>
                    {/* v1.28.0: cliente/contato vinculado ao veículo no snapshot da vistoria */}
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-xs text-muted-foreground shrink-0">Cliente</span>
                      {canOpenCustomer ? (
                        <button
                          type="button"
                          onClick={() => setOwnerModalOpen(true)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary text-right break-words hover:underline underline-offset-2"
                          title="Abrir ficha do cliente/contato"
                        >
                          {ownerName}
                          <Eye className="h-3 w-3 shrink-0" />
                        </button>
                      ) : (
                        <span className="text-xs font-semibold text-foreground text-right break-words">{ownerName}</span>
                      )}
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-xs text-muted-foreground shrink-0">Tipo responsável</span>
                      <div className="flex items-center justify-end gap-1 min-w-0">
                        <span className="text-xs font-semibold text-foreground text-right">{responsibleTypeLabel(data.instance.assignee_mode)}</span>
                        {canEditAssignee && (
                          <Button type="button" size="icon" variant="ghost" className="h-6 w-6 shrink-0" title="Editar responsável" onClick={() => setAssigneeDialogOpen(true)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* v1.22.0: responsável Proprietário vira atalho para a ficha do cliente/contato */}
                    {canOpenOwner ? (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs text-muted-foreground shrink-0">Responsável</span>
                        <button
                          type="button"
                          onClick={() => setOwnerModalOpen(true)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary text-right break-words hover:underline underline-offset-2"
                          title="Abrir ficha do cliente/contato"
                        >
                          {responsibleName(data)}
                          <Eye className="h-3 w-3 shrink-0" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs text-muted-foreground shrink-0">Responsável</span>
                        <div className="min-w-0 text-right">
                          <span className="block text-xs font-semibold text-foreground break-words">{responsibleName(data)}</span>
                          {responsibleEmail(data) && (
                            <span className="block text-[11px] text-muted-foreground break-all">{responsibleEmail(data)}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 xl:min-h-0 xl:overflow-y-auto scrollbar-thin px-5 pb-5">
                  <h3 className="sticky top-0 z-[1] -mx-5 px-5 py-2 bg-muted/20 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Histórico e eventos
                  </h3>
                  <div className="pt-1">
                    <InspectionTimeline events={timeline ?? []} variant="compact" />
                  </div>
                </div>
              </aside>
            </div>

            {/* Rodapé fixo enxuto: motivo à esquerda, ações lado a lado à direita */}
            <div className="border-t bg-card px-4 md:px-6 py-3 flex flex-col md:flex-row md:items-center gap-2">
              {(['awaiting_review', 'pending', 'in_progress', 'correction_requested'].includes(status ?? '') || ((status === 'approved' || status === 'rejected') && reopenMode)) && (
                <Input autoFocus={reopenMode} className="h-8 text-xs md:flex-1 md:max-w-sm md:mr-auto bg-muted/50" placeholder={reopenMode ? 'Motivo da reabertura' : 'Motivo (reprovar, cancelar)'} maxLength={1000}
                  value={reason} onChange={(e) => setReason(e.target.value)} />
              )}
              {/* Botões sempre ancorados à direita no rodapé (ml-auto garante o alinhamento mesmo sem o campo de motivo) */}
              <div className="flex flex-wrap items-center gap-2 md:ml-auto md:justify-end">

                {['pending', 'in_progress', 'awaiting_review', 'correction_requested'].includes(status ?? '') && (
                  <Button size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground"
                    onClick={() => askReason('cancelar', (r, clear) => actions.cancel.mutate({ p_instance_id: instanceId, p_reason: r }, { onSuccess: clear }))}>
                    Cancelar vistoria
                  </Button>
                )}
                {(status === 'approved' || status === 'rejected') && (
                  <Button size="sm" variant="outline" className="h-8 text-xs"
                    onClick={() => {
                      // 1º clique revela o campo; 2º clique confirma a reabertura
                      if (!reopenMode) { setReopenMode(true); return; }
                      askReason('reabrir', (r, clear) => actions.reopen.mutate({ p_instance_id: instanceId, p_reason: r }, { onSuccess: () => { clear(); setReopenMode(false); } }));
                    }}>
                    {reopenMode ? 'Confirmar reabertura' : 'Reabrir'}
                  </Button>
                )}
                {status === 'awaiting_review' && (
                  <>
                    <Button size="sm" variant="outline" className="h-8 text-xs text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => askReason('reprovar', (r, clear) => actions.finalize.mutate({ p_instance_id: instanceId, p_decision: 'reject', p_reason: r }, { onSuccess: clear }))}>
                      Reprovar vistoria
                    </Button>
                    {reviewBlockMsg && (
                      <button type="button" className="text-[11px] text-warning underline-offset-2 hover:underline" onClick={() => setItemFilter('awaiting')}>
                        {reviewBlockMsg}
                      </button>
                    )}
                    <Button size="sm" className="h-8 text-xs bg-success text-success-foreground hover:bg-success/90"
                      disabled={reviewPending > 0 || acceptancePending > 0 || actions.finalize.isPending} title={reviewBlockMsg || undefined}
                      onClick={() => actions.finalize.mutate({ p_instance_id: instanceId, p_decision: 'auto', p_reason: null })}>
                      <Check className="h-3.5 w-3.5 mr-1" />Concluir revisão
                    </Button>
                  </>
                )}
                {(status === 'in_progress' || status === 'correction_requested') && (
                  <>
                    {submitBlockMsg && (
                      <button type="button" className="text-[11px] text-warning underline-offset-2 hover:underline" onClick={() => setItemFilter('pending')}>
                        {submitBlockMsg}
                      </button>
                    )}
                    <Button size="sm" className="h-8 text-xs bg-warning text-warning-foreground hover:bg-warning/90" onClick={handleSubmit}
                      disabled={submitBlockers > 0 || actions.submit.isPending} title={submitBlockMsg || undefined}>
                      {/* v1.28.0: sem revisão o envio já conclui a vistoria */}
                      {requiresReview ? 'Enviar para revisão' : 'Concluir vistoria'}
                    </Button>
                  </>
                )}
                {status === 'pending' && (
                  <Button size="sm" className="h-8 gap-1.5 bg-success text-success-foreground hover:bg-success/90"
                    disabled={actions.start.isPending} onClick={() => actions.start.mutate({ p_instance_id: instanceId })}>
                    {actions.start.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                    Iniciar vistoria
                  </Button>
                )}
              </div>
            </div>
           </div>
        )}
      </DialogContent>
      {/* Mantido dentro do Dialog para herdar o empilhamento automático de modais. */}
      <VehicleDetailsModalPremium
        vehicleId={vehicleModalId}
        open={!!vehicleModalId}
        onOpenChange={(open) => { if (!open) setVehicleModalId(null); }}
      />
      {/*
        v1.22.0: ficha do cliente/contato do responsável (modo Proprietário).
        Com cliente vinculado usa o modo cliente; sem cliente, o modo contato.
      */}
      {ownerModalOpen && (
        <CustomerDetailsModalPremium
          open={ownerModalOpen}
          onOpenChange={setOwnerModalOpen}
          customerId={ownerCustomerId}
          contactId={ownerCustomerId ? null : ownerContactId}
        />
      )}
      {data && data.instance.assignee_mode !== 'provider' && (
        <InspectionAssigneeDialog
          open={assigneeDialogOpen}
          onOpenChange={setAssigneeDialogOpen}
          mode={(data.instance.assignee_mode ?? 'user') as InspectionAssigneeMode}
          userId={(data.instance.executor_id as string | null | undefined) ?? null}
          queueId={(data.instance.assignee_queue_id as string | null | undefined) ?? null}
          isSaving={actions.updateAssignee.isPending}
          onSave={(value) => actions.updateAssignee.mutate({
            p_instance_id: data.instance.id,
            p_mode: value.mode,
            p_user_id: value.mode === 'user' ? value.userId : null,
            p_queue_id: value.mode === 'queue' ? value.queueId : null,
          }, { onSuccess: () => setAssigneeDialogOpen(false) })}
        />
      )}
    </Dialog>
    <ItemRevisionsTimelineDialog answerId={revisionsFor?.id ?? null} itemName={revisionsFor?.name ?? ''}
      onOpenChange={(o) => { if (!o) setRevisionsFor(null); }} />

    {/* v1.21.0: confirmação da troca de versão — explica o impacto antes de aplicar */}
    <AlertDialog open={confirmUpgrade} onOpenChange={setConfirmUpgrade}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Atualizar versão da vistoria</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>
                A vistoria passará da versão <strong>{data?.version.version_number}</strong> para a versão{' '}
                <strong>{data?.version.latest_version_number}</strong> do modelo, aplicando as correções mais recentes.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Respostas dos itens que continuam no modelo são mantidas, com todo o histórico.</li>
                <li>Itens removidos do modelo ficam preservados como histórico e saem da vistoria.</li>
                <li>Itens novos aparecem sem resposta e precisarão ser preenchidos.</li>
                {/* v1.21.1: aceites sempre refeitos ao trocar de versão (conteúdo aceito mudou) */}
                <li>Aceites já coletados nesta etapa precisarão ser coletados novamente.</li>
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={actions.upgrade.isPending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            disabled={actions.upgrade.isPending}
            onClick={(e) => {
              e.preventDefault();
              actions.upgrade.mutate({ p_instance_id: instanceId }, { onSuccess: () => setConfirmUpgrade(false) });
            }}>
            {actions.upgrade.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Atualizar versão
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </TooltipProvider>
  );
}

/** Linha de um item: campo editável, valor atual, anexos e revisão */
function ItemRow({ it, editable, reviewing, pendingStart, geoPerItem, geoRequired, tenantId, instanceId, actions, fraudFlags, highlighted, onOpenRevisions }: {
  it: InspectionItem; editable: boolean; reviewing: boolean; pendingStart: boolean; geoPerItem: boolean; geoRequired: boolean;
  tenantId: string; instanceId: string; actions: ReturnType<typeof useInspectionActions>;
  fraudFlags: InspectionFraudFlag[]; highlighted: boolean; onOpenRevisions: () => void;
}) {
  const type = it.item.field_type;
  const [value, setValue] = useState<any>(it.current?.value ?? it.item.default_value ?? (type === 'multi_select' ? [] : ''));
  const [saving, setSaving] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const options: string[] = it.item.config?.options ?? [];
  const isFile = ['file', 'photo', 'photo_or_file'].includes(type);
  const isDocumentField = type === 'file' || type === 'photo_or_file';
  const cameraOnly = !!it.item.config?.camera_only;
  // Limite de arquivos configurado no item (padrão 20)
  const maxFiles = Math.max(1, Number(it.item.config?.max_files) || 20);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const referencePath = typeof it.item.config?.reference_photo === 'object'
    ? String((it.item.config.reference_photo as { storage_path?: string }).storage_path ?? '') : '';
  const referenceUrl = useSignedUrl(referencePath || null);

  /** Salva o valor ou os anexos informados, mantendo cada versão como revisão. */
  const save = async (capturedFiles: File[] = []) => {
    setSaving(true);
    try {
      const geo = geoPerItem ? await captureGeo() : null;
      if (geoPerItem && geoRequired && !geo) throw new Error('Permita o acesso à localização para salvar');
      const atts = [];
      // from_camera só é verdadeiro quando a foto veio da captura interna (item "Somente câmera")
      for (const f of capturedFiles) atts.push(await uploadInspectionFile(tenantId, instanceId, it.answer_id, f, type === 'photo' && cameraOnly));
      // Em itens com várias fotos, reaproveita as evidências vigentes na nova revisão.
      const persisted = maxFiles > 1 ? it.attachments.map((a) => ({
        storage_path: a.storage_path, file_name: a.file_name, mime_type: a.mime_type,
        size_bytes: a.size_bytes ?? null, sha256: a.sha256 ?? null, from_camera: a.from_camera ?? false,
      })) : [];
      const nextAttachments = [...persisted, ...atts].slice(-maxFiles);
      let v: any = value;
      if (type === 'number') v = value === '' ? null : Number(value);
      if (isFile) v = null;
      await actions.saveAnswer.mutateAsync({
        p_answer_id: it.answer_id, p_value: v, p_geo: geo,
         p_device_info: { ua: navigator.userAgent.slice(0, 200) }, p_attachments: nextAttachments,
      });
      toast.success('Item salvo');
      return true;
    } catch (e: any) {
      toast.error(e.message || 'Erro ao salvar item');
      return false;
    } finally {
      setSaving(false);
    }
  };

  /** Captura, envia e salva sem exigir um segundo clique. */
  const saveCapturedPhoto = async (file: File) => {
    if (!editable || saving) return false;
    return save([file]);
  };

  // Miniatura: sempre o primeiro anexo já salvo (URL assinada) — não há mais
  // pré-visualização local, pois o arquivo é salvo no momento da seleção (v1.18.0).
  const firstImage = it.attachments.find((a) => (a.mime_type ?? '').startsWith('image/'));
  const savedThumb = useSignedUrl(isFile ? firstImage?.storage_path : null);
  const thumb = savedThumb;
  const extraCount = it.attachments.length - 1;
  const [lightbox, setLightbox] = useState(false);
  const [lightboxItems, setLightboxItems] = useState<MediaItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  /** Abre todos os anexos vigentes no visualizador privado, iniciando pelo arquivo escolhido. */
  const openFile = async (attachmentId?: string) => {
    const resolved = await Promise.all(it.attachments.map(async (attachment) => ({
      id: attachment.id,
      url: await signedInspectionUrl(attachment.storage_path),
      type: getMimeMediaType(attachment.mime_type) as MediaItem['type'],
      title: attachment.file_name,
    })));
    const available = resolved.filter((item): item is typeof item & { url: string } => !!item.url);
    if (available.length === 0) {
      toast.error('Não foi possível visualizar o arquivo');
      return;
    }
    const requestedIndex = available.findIndex((item) => item.id === attachmentId);
    setLightboxItems(available.map(({ url, type: mediaType, title }) => ({ url, type: mediaType, title })));
    setLightboxIndex(requestedIndex >= 0 ? requestedIndex : 0);
    setLightbox(true);
  };

  /** Converte o MIME no tipo visual compartilhado de ícone e cor. */
  const visualFileType = (mime?: string | null) => {
    if (mime?.startsWith('image/')) return 'image';
    if (mime === 'application/pdf') return 'pdf';
    if (mime?.includes('spreadsheet') || mime?.includes('excel') || mime?.includes('csv')) return 'spreadsheet';
    if (mime?.includes('document') || mime?.includes('word') || mime?.startsWith('text/')) return 'document';
    return 'other';
  };
  const featuredAttachment = it.attachments[0];
  const hasFileEvidence = it.attachments.length > 0;
  const featuredType = visualFileType(featuredAttachment?.mime_type);
  const FeaturedFileIcon = getFileIcon(featuredType);

  /** Anexos salvos abrem no visualizador privado com URL assinada. */
  const openFeaturedFile = () => {
    void openFile(featuredAttachment?.id);
  };

  // Selo de estado do item
  const stateBadge = it.review_status === 'approved'
    ? { l: 'Aprovado', c: 'border-transparent bg-transparent px-0 text-success', I: Check, approved: true }
    : it.review_status === 'rejected'
      ? { l: 'Reprovado', c: 'bg-destructive/10 text-destructive border-destructive/20', I: X, approved: false }
      : it.current
        ? { l: 'Preenchido', c: 'bg-secondary text-secondary-foreground border-border', I: Check, approved: false }
        : { l: 'Aguardando', c: 'bg-muted text-muted-foreground border-border', I: null, approved: false };

  // v1.17.2: orientação de correção do item reprovado vira tooltip do ícone "?"
  // junto ao status, em vez de um bloco de texto ocupando largura do cartão.
  const rejectHint = it.review_status === 'rejected'
    ? (editable
      ? 'Refazer: registre uma nova evidência e salve o item. A versão reprovada fica no histórico.'
      : reviewing ? 'Aguardando o revisor concluir a revisão para devolver ao vistoriador.' : null)
    : null;

  const takePhotoBtn = cameraOnly && type === 'photo';
  const visualEvidence = type === 'photo' || type === 'photo_or_file';
  const fraudReasons = [...new Set(fraudFlags.map((flag) => FRAUD_LABEL[flag.type] ?? flag.type))];
  const suspiciousAttachmentIds = new Set(fraudFlags.flatMap((flag) =>
    flag.evidences?.flatMap((evidence) => evidence.attachment_ids ?? []) ?? []));

  return (
    <div id={`inspection-item-${it.answer_id}`} className={cn('bg-card rounded-xl p-3 md:p-4 transition-all scroll-mt-6',
      highlighted && 'ring-2 ring-destructive ring-offset-2 ring-offset-background shadow-lg',
      fraudFlags.length > 0 && 'border-destructive/60',
      it.review_status === 'rejected' ? 'border-2 border-destructive/60'
        : it.current ? 'border shadow-sm hover:shadow-md' : 'border-2 border-dashed hover:bg-muted/30')}>
      <div className="flex gap-3 md:gap-4">
        {/* Miniatura grande */}
        {isFile && (!isDocumentField || hasFileEvidence) && (
          <button type="button" disabled={!thumb && it.attachments.length === 0} onClick={openFeaturedFile}
            className={cn('relative shrink-0 rounded-lg border overflow-hidden flex flex-col items-center justify-center transition-colors',
              isDocumentField ? 'h-16 w-16' : 'h-20 w-20 md:h-24 md:w-24',
              hasFileEvidence ? 'border-primary/25 bg-primary/5 hover:border-primary/50' : 'border-dashed bg-muted/50 text-muted-foreground')}>
            {thumb ? (
              <img src={thumb} alt={it.item.name} className="w-full h-full object-cover" />
             ) : hasFileEvidence ? (
               <div className={cn('flex h-full w-full flex-col items-center justify-center', getFileColor(featuredType))}>
                 <FeaturedFileIcon className="mb-1 h-7 w-7" />
                 <span className="text-[9px] font-bold">ARQUIVO</span>
                 <span className="mt-0.5 max-w-[88%] truncate text-[8px] font-medium">Clique para visualizar</span>
               </div>
             ) : (
              <><ImageOff className="h-6 w-6 mb-1 opacity-50" /><span className="text-[9px] font-black">SEM ARQUIVO</span></>
            )}
            {extraCount > 0 && (
              <span className="absolute bottom-1 right-1 rounded bg-foreground/80 text-background text-[10px] font-bold px-1.5">+{extraCount}</span>
            )}
            {fraudFlags.length > 0 && (
              <span className="absolute left-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow" title="Evidência com alerta de fraude">
                <AlertTriangle className="h-3.5 w-3.5" />
              </span>
            )}
          </button>
        )}

        <div className="flex-1 min-w-0">
          {/* v1.17.1: a coluna direita guarda apenas o cabeçalho (título, status, tipo,
              localização, revisão e descrição). Todo o conteúdo do item vem em largura total. */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h4 className="text-sm font-bold">{it.item.name}{it.item.is_required && <span className="text-destructive"> *</span>}</h4>
              {/* v1.17.3: linha ÚNICA de metadados — estado, tipo, localização, revisões e data
                  de salvamento na mesma linha flex-wrap. Antes eram duas linhas separadas
                  que empilhavam verticalmente e desperdiçavam espaço vertical do cartão. */}
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                <span className={cn('inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase', stateBadge.c)}>
                  {stateBadge.approved && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-success" />}
                  {stateBadge.I && <stateBadge.I className={cn('shrink-0', stateBadge.approved ? 'h-2 w-2' : 'h-2.5 w-2.5')} />}{stateBadge.l}
                </span>
                {/* v1.17.2: ícone "?" com a orientação de correção (tooltip) na frente do status */}
                {rejectHint && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" aria-label="Orientação de correção"
                          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-destructive hover:bg-destructive/10">
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" align="start" className="max-w-[260px] text-[11px] font-medium">{rejectHint}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <span>{fieldTypeLabel(type)}</span>
                <MapLinkButton lat={it.current?.geo_lat} lng={it.current?.geo_lng} />
                {it.revisions_count > 0 && (
                  <button type="button" onClick={onOpenRevisions} title="Ver histórico de revisões"
                    className="inline-flex items-center gap-1 text-primary hover:underline underline-offset-2">
                    <Clock className="h-3 w-3" />{it.revisions_count} {it.revisions_count === 1 ? 'revisão' : 'revisões'}
                  </button>
                )}
                {/* v1.23.1: anexo vigente acompanha a revisão na mesma linha; o nome é
                    truncado para preservar a altura compacta do cartão. */}
                {featuredAttachment && (
                  <Button type="button" variant="link" onClick={() => void openFile(featuredAttachment.id)}
                    title={featuredAttachment.file_name}
                    className={cn('h-auto min-w-0 max-w-[150px] justify-start gap-1 truncate p-0 text-[11px] text-current',
                      suspiciousAttachmentIds.has(featuredAttachment.id) && 'text-destructive')}>
                    {suspiciousAttachmentIds.has(featuredAttachment.id)
                      ? <AlertTriangle className="h-3 w-3 shrink-0" />
                      : <Paperclip className={cn('h-3 w-3 shrink-0', getFileColor(featuredType).split(' ')[0])} />}
                    <span className="truncate">{featuredAttachment.file_name}</span>
                    {extraCount > 0 && <span className="shrink-0">+{extraCount}</span>}
                  </Button>
                )}
                {it.current?.created_at && (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap" title="Data em que o item foi salvo">
                    {it.revisions_count > 0 && <span aria-hidden="true">·</span>}
                    Salvo em {new Date(it.current.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                )}
              </div>
              {it.item.description && <p className="text-[11px] text-muted-foreground mt-1">{it.item.description}</p>}
            </div>
            {editable && !takePhotoBtn && !isFile && (
              /* v1.17.4: botão de texto substituído por ícone único de salvar.
                 v1.18.0: itens de arquivo/foto salvam automaticamente ao selecionar,
                 então o ícone permanece apenas nos tipos sem anexo. */
              <button type="button" onClick={() => void save()} disabled={saving}
                title={saving ? 'Salvando item...' : 'Salvar item'} aria-label="Salvar item"
                className="inline-flex h-7 w-7 shrink-0 self-start items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* v1.17.1: motivo da reprovação e orientação de correção ocupam a largura total
          abaixo da miniatura, no mesmo padrão dos demais blocos do item. */}
      {it.review_reason && (
        <p className="mt-2.5 text-[11px] text-destructive border-l-2 border-destructive pl-2">Motivo: {it.review_reason}</p>
      )}


          {editable ? (
            <div className="space-y-2">
              {type === 'yes_no' && (
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 text-[11px]" variant={value === true ? 'default' : 'outline'} onClick={() => setValue(true)}>Sim</Button>
                  <Button size="sm" className="h-7 text-[11px]" variant={value === false ? 'default' : 'outline'} onClick={() => setValue(false)}>Não</Button>
                </div>
              )}
              {type === 'text' && <Textarea rows={2} maxLength={5000} placeholder={it.item.placeholder ?? ''} value={value ?? ''} onChange={(e) => setValue(e.target.value.replace(/<[^>]*>/g, ''))} />}
              {type === 'number' && <Input type="number" className="h-8 text-xs" value={value ?? ''} placeholder={it.item.placeholder ?? ''} onChange={(e) => setValue(e.target.value)} />}
              {type === 'date' && <Input type="date" className="h-8 text-xs" value={value ?? ''} onChange={(e) => setValue(e.target.value)} />}
              {type === 'time' && <Input type="time" className="h-8 text-xs" value={value ?? ''} onChange={(e) => setValue(e.target.value)} />}
              {type === 'datetime' && <Input type="datetime-local" className="h-8 text-xs" value={value ?? ''} onChange={(e) => setValue(e.target.value)} />}
              {type === 'single_select' && (
                <div className="flex flex-wrap gap-1.5">
                  {options.map((o) => <Button key={o} size="sm" className="h-7 text-[11px]" variant={value === o ? 'default' : 'outline'} onClick={() => setValue(o)}>{o}</Button>)}
                </div>
              )}
              {type === 'multi_select' && (
                <div className="flex flex-wrap gap-1.5">
                  {options.map((o) => {
                    const on = Array.isArray(value) && value.includes(o);
                    return <Button key={o} size="sm" className="h-7 text-[11px]" variant={on ? 'default' : 'outline'}
                      onClick={() => setValue(on ? value.filter((x: string) => x !== o) : [...(value || []), o])}>{o}</Button>;
                  })}
                </div>
              )}
              {isFile && (
                visualEvidence ? (
                  // Itens visuais usam o mesmo seletor premium do link externo.
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" className="h-8 text-[11px]" variant={it.attachments.length ? 'secondary' : 'default'}
                      disabled={!editable || saving || (maxFiles > 1 && it.attachments.length >= maxFiles)} onClick={() => setEvidenceOpen(true)}
                      title={pendingStart ? 'Inicie a vistoria para habilitar a câmera' : undefined}>
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                      {saving ? 'Salvando...' : it.attachments.length ? 'Adicionar nova evidência' : 'Adicionar evidência'}
                    </Button>
                    <InspectionEvidenceDrawer open={evidenceOpen} onOpenChange={setEvidenceOpen}
                      itemName={it.item.name} description={it.item.description} referenceUrl={referenceUrl}
                      allowCamera allowFile={!takePhotoBtn}
                      fileLabel={type === 'photo' ? 'Escolher foto da galeria' : 'Escolher foto ou documento'}
                      loading={saving}
                      onCamera={() => { setEvidenceOpen(false); setCameraOpen(true); }}
                      onFile={() => { setEvidenceOpen(false); fileInputRef.current?.click(); }} />
                    {!takePhotoBtn && (
                      <input ref={fileInputRef} type="file" multiple={maxFiles > 1} className="hidden"
                        accept={type === 'photo' ? 'image/*' : undefined}
                        onChange={(e) => {
                          const selected = Array.from(e.target.files ?? []); e.target.value = '';
                          const accepted = selected.filter((f) => f.size <= 20 * 1024 * 1024).slice(0, maxFiles);
                          if (accepted.length < selected.length) toast.warning(`Arquivos inválidos ou acima do limite de ${maxFiles}`);
                          if (accepted.length) void save(accepted);
                        }} />
                    )}
                    <CameraCaptureDialog open={cameraOpen} onOpenChange={setCameraOpen}
                      onCapture={saveCapturedPhoto} />
                    {pendingStart && <span className="text-[10px] text-muted-foreground">Inicie a vistoria para tirar fotos.</span>}
                  </div>
                 ) : (
                   <>
                     {/* O input nativo permanece oculto; a área visual mantém seleção por clique e teclado. */}
                     <input ref={fileInputRef} type="file" multiple={maxFiles > 1} className="hidden"
                     accept={type === 'photo' ? 'image/*' : undefined}
                     onChange={(e) => {
                       const selected = Array.from(e.target.files ?? []);
                       // Permite reselecionar o mesmo arquivo após uma tentativa com erro.
                       e.target.value = '';
                       const sel = selected.filter((f) => f.size <= 20 * 1024 * 1024);
                       if (sel.length < selected.length) toast.warning('Cada arquivo deve ter no máximo 20 MB');
                       if (sel.length > maxFiles) toast.warning(`Máximo de ${maxFiles} arquivo(s)`);
                       const accepted = sel.slice(0, maxFiles);
                       // v1.18.0: selecionar já sobe e salva (mesmo fluxo da captura de foto),
                       // sem botão "Salvar" nem estado "aguardando salvar".
                       if (accepted.length > 0) void save(accepted);
                     }} />
                     {/* Zona de upload compacta em linha única (h-10); com max_files atingido,
                         some até que uma revisão anterior seja substituída. */}
                     {(maxFiles === 1 || it.attachments.length < maxFiles) && (
                       <button type="button" onClick={() => fileInputRef.current?.click()} disabled={saving}
                         className="group flex h-10 w-full items-center gap-2.5 rounded-md border border-dashed border-primary/30 bg-primary/5 px-3 text-left transition-colors hover:border-primary/60 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60">
                         <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                           {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                         </span>
                         <span className="min-w-0 flex-1">
                           <span className="block truncate text-xs font-semibold text-foreground">
                             {saving ? 'Enviando e salvando...' : it.attachments.length > 0
                               ? (maxFiles > 1 ? 'Adicionar outro arquivo' : 'Trocar arquivo')
                               : `Selecionar ${maxFiles > 1 ? 'arquivos' : 'arquivo'}`}
                           </span>
                           <span className="block truncate text-[10px] leading-4 text-muted-foreground">{type === 'photo' ? 'Imagens' : 'Imagens, PDF e documentos'} · até 20 MB · máximo {maxFiles}</span>
                         </span>
                       </button>
                     )}
                   </>
                 )
              )}
            </div>
          ) : (
            isFile && takePhotoBtn && pendingStart ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" className="h-7 text-[11px]" disabled>
                  <Camera className="h-3.5 w-3.5" />Tirar foto
                </Button>
                <span className="text-[10px] text-muted-foreground">Inicie a vistoria para habilitar a câmera.</span>
              </div>
            ) : !isFile && <div className="text-sm font-medium">{displayValue(it.current?.value, type)}</div>
          )}

      {/* v1.17.0: advertência de fraude e lista de anexos saem da coluna direita e ocupam a
          largura total abaixo da miniatura, aproveitando o espaço vazio à esquerda. */}
      {fraudReasons.length > 0 && (
        <div className="mt-2.5 border-l-2 border-destructive pl-2 text-[11px] text-destructive">
          <div className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-3 w-3" />Evidência suspeita</div>
          <div>{fraudReasons.join(' · ')}</div>
        </div>
      )}

      <ImageLightbox open={lightbox} onClose={() => setLightbox(false)} mediaItems={lightboxItems} initialIndex={lightboxIndex} caption={it.item.name} />

      {reviewing && it.item.requires_approval && it.current && (
        <div className="flex flex-wrap items-center gap-2 pt-2.5 mt-3 border-t">
          {/* A decisão vigente fica oculta para destacar somente a alternativa disponível. */}
          {it.review_status !== 'approved' && (
            <Button size="sm" variant="outline" className="h-7 text-[11px] text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/10 hover:text-emerald-700"
              onClick={() => actions.reviewItem.mutate({ p_answer_id: it.answer_id, p_decision: 'approved', p_reason: null }, { onSuccess: () => setRejectReason('') })}>
              <Check className="h-3 w-3 mr-1" />Aprovar
            </Button>
          )}
          {/* v1.12.0: Aprovar e Reprovar lado a lado; o motivo vem logo após o Reprovar. */}
          {it.review_status !== 'rejected' && (
            <>
              {/* Limpa o motivo somente após confirmação do servidor (em erro, o texto é preservado). */}
              <Button size="sm" variant="destructive" className="h-7 text-[11px]" disabled={rejectReason.trim().length < 3 || actions.reviewItem.isPending}
                onClick={() => actions.reviewItem.mutate({ p_answer_id: it.answer_id, p_decision: 'rejected', p_reason: rejectReason.trim() }, { onSuccess: () => setRejectReason('') })}>
                <X className="h-3 w-3 mr-1" />Reprovar
              </Button>
              <Input className="h-7 text-[11px] flex-1 min-w-[140px]" maxLength={1000} placeholder="Motivo da reprovação" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
